/**
 * TudoPets design tokens — single source of truth for theme colors (v2).
 *
 * Strategy: cool chumbo (lead) for text/UI, white/light surfaces,
 * orange600 for brand/actions only — not all warm, not all cool.
 */

// ---------------------------------------------------------------------------
// Brand (orange) — actions, links, primary CTAs
// ---------------------------------------------------------------------------
export const orange600 = '#F8963E';
export const orange800 = '#E97837';
export const orange300 = '#FFBC68';
export const orange100 = '#FFE8D1';
/** @deprecated Prefer orange100 — kept as alias for legacy imports */
export const orangeLight = orange100;

// ---------------------------------------------------------------------------
// Neutral cool (chumbo / lead) — predominant for text and dark UI
// lead* = neutral* in Figma naming
// ---------------------------------------------------------------------------
export const lead600 = '#546E7A';
export const lead800 = '#35454D';
export const lead900 = '#263238';

export const neutral600 = lead600;
export const neutral800 = lead800;
export const neutral900 = lead900;

export const white = '#fff';

// Optional warm page background for A/B — not used as surfaceDefault (v2 = white)
export const gray25 = '#FAF8F5';

// Bottom navigation (Figma pill bar — dark track + inactive icon disks)
export const navBarSurface = lead900;
export const navBarInactiveCircle = '#3D4D56';

// ---------------------------------------------------------------------------
// Surfaces — white page, cool gray inputs/muted (no lavender)
// ---------------------------------------------------------------------------
export const surfaceDefault = white;
export const surfaceCard = white;
export const surfaceElevated = white;
/** Cool light gray — product cards in shop (replaces lavender #F5F2FA) */
export const surfaceProduct = '#F7F9FA';
/** Visible on white page — cool, not warm/lavender */
export const surfaceInput = '#F5F8FA';
export const surfaceMuted = '#ECEFF1';
/** Selected row / address highlight — single warm surface token */
export const surfaceSelectedHighlight = orange100;
export const borderSubtle = '#ECEFF1';

export const starRating = orange300;

// ---------------------------------------------------------------------------
// Warm wash — subtle orange tint on page background (tweakable)
// ---------------------------------------------------------------------------

/** Toggle subtle primary-orange wash on page background (MuiCssBaseline body). */
export const ENABLE_WARM_WASH = true;

/**
 * Opacity for brand accent on white/surface.card.
 * Lower on white bg (0.15–0.18) — wash reads stronger than on gray25.
 */
export const brandAccentAlpha = 0.15;

/** rgba(primaryMain) @ brandAccentAlpha — ripple overlays on light surfaces. */
export const brandAccentRgba = `rgba(248, 150, 62, ${brandAccentAlpha})`;

/** Precomputed blend of orange600 @ brandAccentAlpha over white (#FFEFE2 ≈ @ 0.15). */
export const brandAccentOnWhite = brandAccentRgba;

/** Documented wash token (same hue as ripple, tuned for page background). */
export const warmWashOverlay = brandAccentOnWhite;

/**
 * Top wash: rgba gradient fades quickly so the page stays clean.
 */
export const warmWashBackgroundImage =
  'linear-gradient(180deg, rgba(248, 150, 62, 0.24) 10%, transparent 40%)';

// ---------------------------------------------------------------------------
// Layout radius tokens
// ---------------------------------------------------------------------------
export const layoutCardRadius = '16px';
export const layoutShopSheetRadius = '32px';
export const layoutPillRadius = '40px';
/** Full-width CTA buttons (login, checkout, add pet, etc.) */
export const layoutButtonPillRadius = '100px';
/** Icon / back buttons */
export const layoutIconButtonRadius = '14px';
/** Fixed bottom sheet bars (shopping cart summary) */
export const layoutSheetTopRadius = '20px';

// ---------------------------------------------------------------------------
// Palette convenience aliases
// ---------------------------------------------------------------------------
export const primaryMain = orange600;
export const primaryDark = orange800;
export const primaryLight = orange300;
export const contrastText = white;
export const backgroundDefault = surfaceDefault;
export const backgroundPaper = surfaceCard;

// ---------------------------------------------------------------------------
// WASH_PRESETS — tweak ENABLE_WARM_WASH, brandAccentAlpha, gradient stop %
// ---------------------------------------------------------------------------
// Subtle (default):          ENABLE_WARM_WASH=true,  alpha=0.15, rgba(248,150,62,0.24) @ 40%
// Medium ripple:             brandAccentAlpha=0.18
// Strong:                    rgba(248,150,62,0.28) in warmWashBackgroundImage
// Off:                       ENABLE_WARM_WASH=false — flat body bg only
// Warm page A/B:             body #FEF6EF or gray25, wash 0.18–0.24
